import React from 'react';
import {
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';

import { UsageMetricsResponse } from 'src/types/business-intelligence/EmailPOMetrics';
import EmptyState from './EmptyState';

interface MetricsChartsProps {
  data: UsageMetricsResponse;
  loading: boolean;
}

// Reusable card wrapper that toggles between chart and table views
interface ChartTableCardProps {
  title: string;
  hasData: boolean;
  chart: React.ReactNode;
  table: React.ReactNode;
  emptyState: React.ReactNode;
}

const ChartTableCard: React.FC<ChartTableCardProps> = ({
  title,
  hasData,
  chart,
  table,
  emptyState,
}) => {
  const [mode, setMode] = React.useState<'chart' | 'table'>('chart');

  const toggleMode = () =>
    setMode((prev) => (prev === 'chart' ? 'table' : 'chart'));

  return (
    <Card>
      <MDBox p={3} height="100%" display="flex" flexDirection="column">
        {/* Title & Toggle */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Grid item>
            <MDTypography variant="h6" fontWeight="medium">
              {title}
            </MDTypography>
          </Grid>
          <Grid item>
            <IconButton
              aria-label={
                mode === 'chart' ? 'Show table view' : 'Show chart view'
              }
              onClick={toggleMode}
              size="small"
            >
              {mode === 'chart' ? <TableChartIcon /> : <BarChartIcon />}
            </IconButton>
          </Grid>
        </Grid>

        {/* Content */}
        <MDBox height="300px" overflow="auto">
          {!hasData ? emptyState : mode === 'chart' ? chart : table}
        </MDBox>
      </MDBox>
    </Card>
  );
};

// Table components --------------------------------------------------

const SalesOrdersByUserTable: React.FC<{
  rows: UsageMetricsResponse['salesOrdersCreatedByGently']['byUser'];
  formatNumber: (val: number) => string;
}> = ({ rows, formatNumber }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>User</TableCell>
        <TableCell align="right">Sales Orders</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.userId}>
          <TableCell>{row.userName}</TableCell>
          <TableCell align="right">{formatNumber(row.count)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const SalesOrdersByCustomerTable: React.FC<{
  rows: UsageMetricsResponse['salesOrdersCreatedByGently']['byCustomer'];
  formatNumber: (val: number) => string;
}> = ({ rows, formatNumber }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Customer</TableCell>
        <TableCell align="right">Sales Orders</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.customerId}>
          <TableCell>{row.customerName}</TableCell>
          <TableCell align="right">{formatNumber(row.count)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

type TimeSeriesTableProps<T extends { period: string }> = {
  rows: T[];
  columns: TimeSeriesTableColumns<T>;
};

type TimeSeriesTableColumns<T> = readonly {
  [K in keyof T]: {
    key: K;
    label: string;
    formatter?: (value: T[K]) => string;
  };
}[keyof T][];

function TimeSeriesTable<T extends { period: string }>({
  rows,
  columns,
}: TimeSeriesTableProps<T>) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Period</TableCell>
          {columns.map((col) => (
            <TableCell key={String(col.key)} align="right">
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.period}`}>
            <TableCell>{`${row.period}`}</TableCell>
            {columns.map((col) => (
              <TableCell key={String(col.key)} align="right">
                {col.formatter
                  ? col.formatter(row[col.key])
                  : String(row[col.key])}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const MetricsCharts: React.FC<MetricsChartsProps> = ({ data, loading }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  const userCustomerTimeSeries = React.useMemo(() => {
    if (
      loading ||
      !data?.activeUsers?.timeSeries ||
      !Array.isArray(data.activeUsers.timeSeries)
    ) {
      return [];
    }

    const map = new Map<
      string,
      { period: string; users: number; customers: number }
    >();

    data.activeUsers.timeSeries.forEach((t) => {
      map.set(t.period, {
        period: t.period,
        users: t.count,
        customers: 0,
      });
    });

    const customerSeries = data.activeCustomers?.timeSeries;
    if (customerSeries && Array.isArray(customerSeries)) {
      customerSeries.forEach((t) => {
        const entry = map.get(t.period) || {
          period: t.period,
          users: 0,
          customers: 0,
        };
        entry.customers = t.count;
        map.set(t.period, entry);
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      a.period.localeCompare(b.period),
    );
  }, [loading, data]);

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card sx={{ height: '400px' }}>
              <MDBox
                p={3}
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <MDTypography variant="body2" color="text">
                  Loading chart...
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Sales Orders by User */}
      <Grid item xs={12} md={6}>
        <ChartTableCard
          title="Sales Orders by User"
          hasData={(data.salesOrdersCreatedByGently?.byUser ?? []).length > 0}
          chart={
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.salesOrdersCreatedByGently?.byUser ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="userName" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number) => [value, 'Sales Orders']}
                />
                <Bar dataKey="count" fill="#66bb6a" />
              </BarChart>
            </ResponsiveContainer>
          }
          table={
            <SalesOrdersByUserTable
              rows={data.salesOrdersCreatedByGently?.byUser ?? []}
              formatNumber={formatNumber}
            />
          }
          emptyState={
            <EmptyState
              icon="person"
              title="No User Data"
              message="No sales orders have been created by users in selected period."
            />
          }
        />
      </Grid>

      {/* Top Customers by Sales Orders */}
      <Grid item xs={12} md={6}>
        <ChartTableCard
          title="Top Customers by Sales Orders"
          hasData={
            (data.salesOrdersCreatedByGently?.byCustomer ?? []).length > 0
          }
          chart={
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.salesOrdersCreatedByGently?.byCustomer ?? []}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="customerName" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number) => [value, 'Sales Orders']}
                />
                <Bar dataKey="count" fill="#ab47bc" />
              </BarChart>
            </ResponsiveContainer>
          }
          table={
            <SalesOrdersByCustomerTable
              rows={data.salesOrdersCreatedByGently?.byCustomer ?? []}
              formatNumber={formatNumber}
            />
          }
          emptyState={
            <EmptyState
              icon="store"
              title="No Customer Data"
              message="No sales orders have been created for customers in selected period."
            />
          }
        />
      </Grid>

      {/* Famous Errors Trend */}
      <Grid item xs={12} md={6}>
        <ChartTableCard
          title="Famous Errors Trend"
          hasData={(data.famousErrors?.timeSeries ?? []).length > 0}
          chart={
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.famousErrors?.timeSeries ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number) => [formatNumber(value), 'Errors']}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="Errors"
                  stroke="#ef5350"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          }
          table={
            <TimeSeriesTable
              rows={data.famousErrors?.timeSeries ?? []}
              columns={[
                { key: 'count', label: 'Errors', formatter: formatNumber },
              ]}
            />
          }
          emptyState={
            <EmptyState
              icon="error"
              title="No Error Data"
              message="No Famous error data available for the selected period."
            />
          }
        />
      </Grid>

      {/* User & Customer Activity (Time Series) */}
      <Grid item xs={12} md={6}>
        <ChartTableCard
          title="User & Customer Activity"
          hasData={userCustomerTimeSeries.length > 0}
          chart={
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userCustomerTimeSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    formatNumber(value as number),
                    name,
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="users"
                  name="Active Users"
                  stroke="#42a5f5"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="customers"
                  name="Customers w/ Orders"
                  stroke="#26c6da"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          }
          table={
            <TimeSeriesTable
              rows={userCustomerTimeSeries}
              columns={[
                {
                  key: 'users',
                  label: 'Active Users',
                  formatter: formatNumber,
                },
                {
                  key: 'customers',
                  label: 'Customers w/ Orders',
                  formatter: formatNumber,
                },
              ]}
            />
          }
          emptyState={
            <EmptyState
              icon="group"
              title="No Activity Data"
              message="No user or customer activity data available for the selected period."
            />
          }
        />
      </Grid>

      {/* Sales Orders Trend */}
      <Grid item xs={12} md={6}>
        <ChartTableCard
          title="Sales Orders Trend"
          hasData={
            (data.salesOrdersCreatedByGently?.timeSeries ?? []).length > 0
          }
          chart={
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data.salesOrdersCreatedByGently?.timeSeries ?? []}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number) => [
                    formatNumber(value),
                    'Sales Orders',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#66bb6a"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          }
          table={
            <TimeSeriesTable
              rows={data.salesOrdersCreatedByGently?.timeSeries ?? []}
              columns={[
                {
                  key: 'count',
                  label: 'Sales Orders',
                  formatter: formatNumber,
                },
              ]}
            />
          }
          emptyState={
            <EmptyState
              icon="trending_up"
              title="No Trend Data"
              message="No time series data available for the selected period."
            />
          }
        />
      </Grid>

      {/* Sales Value Trend */}
      <Grid item xs={12} md={6}>
        <ChartTableCard
          title="Sales Value Trend"
          hasData={
            (data.salesOrdersCreatedByGently?.timeSeries ?? []).length > 0
          }
          chart={
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data.salesOrdersCreatedByGently?.timeSeries ?? []}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value: number) => [
                    formatCurrency(value),
                    'Value',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#ff9800"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          }
          table={
            <TimeSeriesTable
              rows={data.salesOrdersCreatedByGently?.timeSeries ?? []}
              columns={[
                { key: 'value', label: 'Value', formatter: formatCurrency },
              ]}
            />
          }
          emptyState={
            <EmptyState
              icon="attach_money"
              title="No Value Trend Data"
              message="No time series value data available for the selected period."
            />
          }
        />
      </Grid>
    </Grid>
  );
};

export default MetricsCharts;
